import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Building2, LayoutDashboard, Home, MessageSquare, LogOut, Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export const AdminLayout = ({ children, user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      toast.success('Logged out successfully');
      navigate('/admin-portal/login');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Failed to logout');
    }
  };

  const navigation = [
    { name: 'Dashboard', href: '/admin-portal/dashboard', icon: LayoutDashboard },
    { name: 'Properties', href: '/admin-portal/properties', icon: Home },
    { name: 'Testimonials', href: '/admin-portal/testimonials', icon: MessageSquare },
    { name: 'Inquiries', href: '/admin-portal/inquiries', icon: MessageSquare },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      {/* Top Bar */}
      <div className="bg-white border-b border-stone-200 sticky top-0 z-40">
        <div className="flex items-center justify-between h-16 px-6">
          <div className="flex items-center gap-4">
            <button
              data-testid="mobile-sidebar-toggle"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 text-primary"
            >
              {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            <Link to="/admin-portal/dashboard" className="flex items-center gap-3">
              <img
                src="https://customer-assets.emergentagent.com/job_realty-vision-4/artifacts/xju4cezs_sara-realty-logo.png"
                alt="Sara Realty"
                className="h-10 w-auto"
              />
              <span className="font-heading text-xl font-medium text-primary hidden sm:block">Sara Realty Admin</span>
            </Link>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              {user?.picture && (
                <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
              )}
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium text-primary">{user?.name}</p>
                <p className="text-xs text-[#57534E]">{user?.email}</p>
              </div>
            </div>
            <Button
              data-testid="logout-button"
              onClick={handleLogout}
              variant="ghost"
              size="sm"
              className="text-primary hover:bg-primary/10"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
            } lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-30 w-64 bg-white border-r border-stone-200 transition-transform duration-300 pt-16 lg:pt-0`}
        >
          <nav className="p-6 space-y-2">
            {navigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  data-testid={`sidebar-${item.name.toLowerCase()}`}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${isActive(item.href)
                      ? 'bg-primary text-white'
                      : 'text-[#57534E] hover:bg-[#FFFBF5]'
                    }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.name}</span>
                </Link>
              );
            })}
          </nav>
          <div className="absolute bottom-6 left-6 right-6">
            <Link
              to="/"
              data-testid="back-to-website"
              className="block text-center py-2 text-sm text-primary hover:underline"
            >
              ← Back to Website
            </Link>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-12">
          {children}
        </main>
      </div>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-20 pt-16"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}
    </div>
  );
};